import { createContext, forwardRef, useCallback, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// URL scheme: English at the root, other languages under a prefix
//   /shop  ·  /ru/shop  ·  /it/shop
export const LANGS = ['en', 'ru', 'it'];
export const DEFAULT_LANG = 'en';
const PREFIX_RE = /^\/(ru|it)(?=\/|$)/;
const LANG_KEY = 'lang';

export const localeFromPath = (pathname = '') => pathname.match(PREFIX_RE)?.[1] || DEFAULT_LANG;

export const stripLocale = (pathname = '/') => pathname.replace(PREFIX_RE, '') || '/';

export const localizePath = (path, lang) => {
  if (lang === DEFAULT_LANG || typeof path !== 'string' || !path.startsWith('/')) return path;
  return path === '/' ? `/${lang}/` : `/${lang}${path}`;
};

// Link `to` values: strings or { pathname, search, hash }
const localizeTo = (to, lang) => {
  if (typeof to === 'string') return localizePath(to, lang);
  if (to && typeof to === 'object' && to.pathname) return { ...to, pathname: localizePath(to.pathname, lang) };
  return to;
};

export const LocaleContext = createContext(DEFAULT_LANG);
export const useLocale = () => useContext(LocaleContext);

// Drop-in for react-router's <Link>: keeps the visitor in their language
export const LocalLink = forwardRef(({ to, ...rest }, ref) => {
  const lang = useLocale();
  return <Link ref={ref} to={localizeTo(to, lang)} {...rest} />;
});
LocalLink.displayName = 'LocalLink';

// Drop-in for useNavigate()
export const useLocalNavigate = () => {
  const navigate = useNavigate();
  const lang = useLocale();
  return useCallback((to, opts) => (typeof to === 'number' ? navigate(to) : navigate(localizeTo(to, lang), opts)), [navigate, lang]);
};

export const storeLangChoice = (lang) => {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    // ignore
  }
};

// Language a visitor arriving on an unprefixed (English) URL should be sent to:
// their explicit earlier choice, else their browser language on a first visit.
export const preferredLang = () => {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored) return LANGS.includes(stored.slice(0, 2)) ? stored.slice(0, 2) : DEFAULT_LANG;
  } catch {
    // ignore
  }
  const nav = (typeof navigator !== 'undefined' && (navigator.languages?.[0] || navigator.language)) || '';
  const base = nav.slice(0, 2).toLowerCase();
  return LANGS.includes(base) ? base : DEFAULT_LANG;
};
